import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  Typography,
  Grid,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred'
import { IFacebookChatRoom } from '../../../types/facebook'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import dayjs from 'dayjs'
import { axiosInstance } from '../../../hooks/useAxios'
import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { message, Table } from 'antd'
import { useDispatch } from 'react-redux'
import {
  closeModal,
  openModal,
} from '../../../../store/reducers/modal/modal.slice'
import ConfirmOrderModal from '../../../components/ConfirmOrderModal'
import { OrderFormAddProductModal } from '../../Order/Features/OrderList/components/OrderFormAddProductModal'
import { IProduct } from '../../../types/product'
import { validateMinMaxValue } from '../../../utils/validateMinMaxValue'
import UserSelectSearch from '../../../components/SelectSearch/UserSelectSearch'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ONLY_DATE_FORMAT } from '../../../constants/global'
import InputUpload from '../../../components/InputUpload'
interface Props {
  chatRoom?: IFacebookChatRoom | null
}

const getOrderFormValidationSchema = () => {
  return Yup.object({
    name: Yup.string(),
    phone: Yup.string(),
    address: Yup.string(),
    subDistrict: Yup.string(),
    district: Yup.string(),
    province: Yup.string(),
    zipcode: Yup.string(),
    orderDate: Yup.string().required('กรุณาเลือกวันที่สั่งซื้อ'),
    products: Yup.array(
      Yup.object({
        product: Yup.object().required('กรุณาเลือกสินค้า'),
        quantity: Yup.number().required('กรุณากรอกจำนวนสินค้า'),
        discount: Yup.number().min(0, 'กรุณากรอกส่วนลดให้ถูกต้อง'),
      }),
    ).min(1, 'กรุณาเลือกสินค้าอย่างน้อย 1 รายการ'),
    isCOD: Yup.number().required(),
    paymentDate: Yup.string(),
    distribution: Yup.string(),
    paymentUrl: Yup.string(),
    note: Yup.string(),
    commissionUsername: Yup.string(),
    assignTelesaleBy: Yup.string(),
  })
}

const OrderPanel = (props: Props) => {
  const { chatRoom } = props
  const [isModalAddProduct, setIsModalAddProduct] = useState<boolean>(false)
  const [resetAutoComplete, setResetAutoComplete] = useState<string>('')
  const [disabled, setDisabled] = useState<boolean>(false)

  const dispatch = useDispatch()

  const formik = useFormik<any>({
    initialValues: {
      name: '',
      phone: '',
      address: '',
      subDistrict: '',
      district: '',
      province: '',
      zipcode: '',
      isCOD: 1,
      products: [],
      orderDate: dayjs().format(),
      distribution: '',
      paymentDate: '',
      paymentUrl: '',
      note: '',
      commissionUsername: '',
      assignTelesaleBy: '',
    },
    validationSchema: getOrderFormValidationSchema(),
    onSubmit: async (values: any) => {
      try {
        await axiosInstance.post(`/order`, {
          name: values.name.trim() as string,
          phone: values.phone.trim() as string,
          address: {
            address: values.address.trim() as string,
            subDistrict: values.subDistrict.trim() as string,
            district: values.district.trim() as string,
            province: values.province.trim() as string,
            zipcode: `${values.zipcode}`,
          },
          isCOD: !!values.isCOD,
          products: values.products.map((product: any) => ({
            objectId: product.product?.objectId,
            quantity: product.quantity,
            discount: product.discount,
          })),
          orderDate: values.orderDate,
          commissionUsername: values.commissionUsername,
          distribution: values.distribution,
          paymentDate: values.paymentDate,
          paymentUrl: values.paymentUrl,
          note: values.note,
          assignTelesaleBy: values.assignTelesaleBy,
          referrer: {
            orderId: chatRoom?.objectId,
            platform: 'facebook',
          },
        })
        formik.resetForm()
        message.success('สร้างสินค้าสำเร็จ')
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
    },
  })

  const handleCheckCustomerWarning = async (values: any) => {
    try {
      const responseCustomerWarning = await axiosInstance.post(
        '/blacklists/checking',
        {
          name: values.name,
          phoneNumber: values.phone,
        },
      )
      if (responseCustomerWarning.data.blacklist) {
        dispatch(
          openModal({
            title: `เตือน! ลูกค้า ${values.name} อยู่ในรายการ Blacklist`,
            content: (
              <ConfirmOrderModal
                onConfirm={async () => {
                  await formik.handleSubmit()
                  dispatch(closeModal())
                }}
              />
            ),
          }),
        )
      } else {
        formik.handleSubmit()
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  useEffect(() => {
    formik.setValues({
      name: chatRoom?.fullName ?? '',
      phone: chatRoom?.phone ?? '',
      address: chatRoom?.address ?? '',
      subDistrict: chatRoom?.subDistrict ?? '',
      district: chatRoom?.district ?? '',
      province: chatRoom?.province ?? '',
      zipcode: chatRoom?.zipcode ?? '',
      isCOD: 1,
      products: [],
      orderDate: dayjs().format(),
      paymentDate: '',
      paymentUrl: '',
      note: '',
      commissionUsername: '',
      assignTelesaleBy: '',
    })
    setDisabled(!chatRoom?.fullName || !chatRoom?.phone)
  }, [chatRoom])

  const handleAddCollection = (value: any) => {
    const prevCollections = formik.values.products
    formik.setFieldValue('products', [...prevCollections, value])
  }

  const onChangeDiscount = (index: number, value: any) => {
    const prevCollections = formik.values.products
    prevCollections[index].discount = value
    formik.setFieldValue('products', prevCollections)
  }

  const handleDeleteCollectionByIndex = (index: number) => {
    const prevCollections = formik.values.products
    formik.setFieldValue(
      'products',
      prevCollections.filter((_: any, i: number) => i !== index),
    )
  }

  const getTotal = () => {
    return formik.values.products.reduce(
      (total: number, product: any) =>
        total +
        (product.product?.price! * product.quantity! - product.discount! || 0),
      0,
    )
  }

  const handleUploadClick = async (event: any) => {
    const file = event.target.files[0]
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await axiosInstance.post<any>('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.status !== 201) {
        return message.error('อัพโหลดรูปภาพไม่สำเร็จ')
      }
      await formik.setFieldValue('paymentUrl', response.data.url)
      return message.success('อัพโหลดรูปภาพสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const columns = [
    {
      title: 'รายการ',
      dataIndex: 'product',
      key: 'name',
      render: (product: IProduct, record: any) => {
        return (
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              {product?.name} - {product?.code}
            </Typography>
            <Typography fontSize={12} lineHeight={1}>
              <strong>ราคา:</strong> {(product?.price || 0).toLocaleString()}
            </Typography>
            <Typography fontSize={12} lineHeight={1}>
              <strong>จำนวน:</strong>{' '}
              {Math.abs(record?.quantity || 0).toLocaleString()}
            </Typography>
            <Typography fontSize={12} lineHeight={1}>
              <strong>ราคา:</strong>{' '}
              {(
                product!.price * record.quantity! - record.discount! || 0
              ).toLocaleString()}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'key',
      key: 'discount',
      width: 120,
      align: 'center' as const,
      render: (key?: number) => (
        <TextField
          fullWidth
          label="ส่วนลด"
          name="discount"
          value={formik.values.products[key!].discount || ''}
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*', '.'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onChange={(e) => {
            onChangeDiscount(
              key!,
              validateMinMaxValue(
                e.target.value,
                0,
                formik.values.products[key!].product!.price || 1,
              ) || '',
            )
          }}
          helperText={
            formik.touched.products && (formik.errors.products as string)
          }
          error={!!(formik.touched.products && formik.errors.products)}
        />
      ),
    },
    {
      dataIndex: 'key',
      key: 'delete',
      width: 50,
      align: 'center' as const,
      render: (key: number) => (
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <IconButton onClick={() => handleDeleteCollectionByIndex(key)}>
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      ),
    },
  ]

  return (
    <>
      <OrderFormAddProductModal
        onAdd={handleAddCollection}
        isOpen={isModalAddProduct}
        onClose={(visible) => setIsModalAddProduct(visible)}
      />
      <Box>
        {disabled && (
          <Alert severity="warning">
            กรุณากรอกข้อมูลลูกค้าให้ครบถ้วน ในแท็บข้อมูล
          </Alert>
        )}
        <Grid container spacing={1} sx={{ padding: 2 }}>
          <Grid item xs={12} lg={12}>
            <Grid
              container
              spacing={2}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Grid item>
                <Typography variant={'h6'}>รายการสินค้า</Typography>
              </Grid>
              <Grid item>
                <Button
                  disabled={disabled || formik.isSubmitting}
                  startIcon={<AddIcon />}
                  color="warning"
                  size="medium"
                  variant="contained"
                  onClick={() => setIsModalAddProduct(!isModalAddProduct)}
                >
                  เพิ่มสินค้า
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <Table
              size={'small'}
              columns={columns}
              dataSource={formik.values.products?.map(
                (product: any, index: number) => {
                  return {
                    ...product,
                    key: index,
                  }
                },
              )}
              bordered
              pagination={{ hideOnSinglePage: true }}
            />
          </Grid>
          <Grid container justifyContent={'flex-end'} mt={2}>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                ราคารวม: {getTotal().toLocaleString()} บาท
              </Typography>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <UserSelectSearch
              label={'ลงออเดอร์ (ค่าคอม) ให้พนักงาน'}
              onSelect={(e) =>
                formik.setFieldValue('commissionUsername', e?.value.username)
              }
              name="commissionUsername"
              helperText={
                formik.touched.commissionUsername &&
                (formik.errors.commissionUsername as string)
              }
              error={
                !!(
                  formik.touched.commissionUsername &&
                  formik.errors.commissionUsername
                )
              }
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <UserSelectSearch
              resetAutoComplete={resetAutoComplete}
              label={'ผู้ดูแล Telesale (ไม่จำเป็นต้องระบุ)'}
              onSelect={(e) =>
                formik.setFieldValue('assignTelesaleBy', e?.value.username)
              }
              name="assignTelesaleBy"
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <Select
              id="isCOD"
              name="isCOD"
              placeholder="การชำระเงิน"
              value={formik.values.isCOD}
              onChange={formik.handleChange}
              fullWidth
            >
              <MenuItem value={1}>เก็บเงินปลายทาง</MenuItem>
              <MenuItem value={0}>ชำระเงินผ่านธนาคาร</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} lg={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format={ONLY_DATE_FORMAT}
                disableFuture
                label="วันที่สั่งซื้อ"
                name="orderDate"
                value={
                  formik.values.orderDate
                    ? dayjs(formik.values.orderDate)
                    : null
                }
                onChange={(e) => {
                  formik.setFieldValue('orderDate', e?.format())
                }}
                sx={{ width: '100%' }}
                slotProps={{
                  textField: {
                    helperText:
                      formik.touched.orderDate &&
                      (formik.errors.orderDate as string),
                    error: !!(
                      formik.touched.orderDate && formik.errors.orderDate
                    ),
                    onBlur: formik.handleBlur,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          {formik.values.isCOD === 0 && (
            <>
              <Grid item xs={12} lg={12}>
                <InputUpload
                  onRemove={() => formik.setFieldValue('paymentUrl', '')}
                  handleUploadClick={handleUploadClick}
                  imageUrl={formik.values.paymentUrl ?? ''}
                />
              </Grid>
              <Grid item xs={12} lg={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format={ONLY_DATE_FORMAT}
                    disableFuture
                    label="วันที่ชำระ"
                    name="paymentDate"
                    value={
                      formik.values.paymentDate
                        ? dayjs(formik.values.paymentDate)
                        : null
                    }
                    onChange={(e) => {
                      formik.setFieldValue('paymentDate', e?.format())
                    }}
                    sx={{ width: '100%' }}
                    slotProps={{
                      textField: {
                        helperText:
                          formik.touched.paymentDate &&
                          (formik.errors.paymentDate as string),
                        error: !!(
                          formik.touched.paymentDate &&
                          formik.errors.paymentDate
                        ),
                        onBlur: formik.handleBlur,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}
          <Grid item xs={12} lg={12}>
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReportGmailerrorredIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
              label={`หมายเหตุ`}
              name="note"
              helperText={formik.touched.note && (formik.errors.note as string)}
              error={!!(formik.touched.note && formik.errors.note)}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.note}
            />
          </Grid>
          <Grid item xs={12} lg={12}>
            <Button
              disabled={disabled || formik.isSubmitting}
              color="primary"
              onClick={() => handleCheckCustomerWarning(formik.values)}
              size="large"
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
            >
              สร้างออเดอร์
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default OrderPanel
